import React from 'react'
import { Modal } from './Modal'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  tone?: 'danger' | 'warning' | 'primary'
  loading?: boolean
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  tone = 'primary',
  loading = false,
}) => {
  const toneButtonClasses = {
    danger: 'bg-danger hover:bg-danger-hover text-white focus:ring-danger',
    warning: 'bg-status-idle hover:bg-status-idle/90 text-white focus:ring-status-idle',
    primary: 'bg-primary hover:bg-primary-hover text-primary-foreground focus:ring-primary',
  }

  const toneBadgeClasses = {
    danger: 'bg-danger/10 text-danger',
    warning: 'bg-status-idle/10 text-status-idle',
    primary: 'bg-primary/10 text-primary',
  }

  const footerActions = (
    <>
      <button
        onClick={onClose}
        disabled={loading}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
      >
        {cancelText}
      </button>
      <button
        onClick={onConfirm}
        disabled={loading}
        className={`px-4 py-2 text-sm font-semibold rounded-btn transition-colors focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center min-w-[5rem] ${toneButtonClasses[tone]}`}
      >
        {loading ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          confirmText
        )}
      </button>
    </>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm" footer={footerActions}>
      <div className="flex items-start gap-4">
        {/* Warning Icon Badge */}
        <div className={`p-2 rounded-full flex-shrink-0 ${toneBadgeClasses[tone]}`}>
          {tone === 'danger' && (
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          )}
          {tone === 'warning' && (
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          )}
          {tone === 'primary' && (
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          )}
        </div>

        <div className="space-y-1">
          <p className="text-text-secondary leading-relaxed">{message}</p>
        </div>
      </div>
    </Modal>
  )
}
